import { isAuthError } from '@brighte/passwordless-authenticator';
import { datadogRum } from '@datadog/browser-rum';
import { KNOWN_ERROR_MESSAGES as ERROR, KnownErrors, getErrorMessage } from '@utils';
import { ErrorResponse } from '../../graphql';

type ErrorResult = { message: string; code?: number };

const defaultError = {
  message: ERROR.DEFAULT,
};

const errorsMap = {
  [KnownErrors.NotFound]: ERROR.INVALID_QUOTE,
  [KnownErrors.ValidationError]: ERROR.VALIDATION_ERROR,
  [KnownErrors.QuoteExpired]: ERROR.QUOTE_EXPIRED,
};

export function getErrorDetails(error: ErrorResponse): ErrorResult {
  datadogRum.addAction('Error Fallback', {
    name: 'Error Fallback',
    values: {
      error,
    },
  });

  if (isAuthError(error)) {
    return { code: 401, message: 'UNAUTHENTICATED' };
  }

  const graphqlError = error?.response?.errors?.[0];

  if (!graphqlError) {
    return defaultError;
  }

  const errorMessage = getErrorMessage(graphqlError);
  const errorDetails = graphqlError?.extensions;

  //Known errors
  if (errorsMap[errorMessage]) {
    return {
      message: errorsMap[errorMessage],
    };
  }

  //Http errors
  if (errorDetails?.response) {
    const { status, body } = errorDetails.response;
    switch (status) {
      case 401:
        return { code: status, message: 'UNAUTHENTICATED' };
      case 500:
        return { code: status, message: ERROR.INTERNAL_SERVER_ERROR };
      case 404:
        return { code: status, message: ERROR.USER_NOT_FOUND };
      default:
        return { code: status, message: body?.code || body?.message || ERROR.DEFAULT };
    }
  }

  //Auth errors
  if (errorDetails?.code) {
    switch (errorDetails.code) {
      case 'UNAUTHORIZED':
        return {
          code: errorDetails.statusCode,
          message: errorDetails.message?.includes('suspended') ? ERROR.USER_SUSPENDED : ERROR.FORBIDDEN,
        };
      case 'UNAUTHENTICATED':
        return { code: errorDetails.statusCode, message: 'UNAUTHENTICATED' };
      case 'FORBIDDEN':
        return { code: 403, message: ERROR.FORBIDDEN };
      default:
        return defaultError;
    }
  }

  return defaultError;
}
